import type {
  GmailSendPayload,
  ProposalDetail,
  ProposedAction,
} from '@/lib/proposals/core/types';
import { isGmailSendPayload } from '@/lib/proposals/core/types';

/**
 * Encode/decode the email-thread composite entry id.
 *
 * The unified slideout stack stores a single string id per entry. The
 * email-thread kind needs both the proposal id (to look up the action)
 * and the action id (the gmail_send action whose thread we're viewing),
 * so we pack them as `${proposalId}-${actionId}`. Proposal and action
 * ids are integers from the database and won't contain hyphens, so the
 * delimiter is unambiguous.
 */

// Match a sequence of one or more ASCII digits with no leading zero (or a
// single "0", though valid ids are always > 0). This rejects floats,
// negative numbers, scientific notation (`1e10`), hex (`0x10`), and the
// empty string — all of which `Number()` would otherwise coerce silently.
const POSITIVE_INTEGER_RE = /^(?:[1-9][0-9]*|0)$/;

function parsePositiveInteger(part: string): number | null {
  if (!POSITIVE_INTEGER_RE.test(part)) {
    return null;
  }
  const parsed = Number(part);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export function encodeEmailThreadEntryId(
  proposalId: number,
  actionId: number
): string {
  return `${proposalId}-${actionId}`;
}

export function decodeEmailThreadEntryId(
  id: string
): { proposalId: number; actionId: number } | null {
  const dashIdx = id.indexOf('-');
  if (dashIdx === -1) {
    return null;
  }
  const proposalId = parsePositiveInteger(id.slice(0, dashIdx));
  const actionId = parsePositiveInteger(id.slice(dashIdx + 1));
  if (proposalId === null || actionId === null) {
    return null;
  }
  return { proposalId, actionId };
}

/**
 * The gmail_send action whose thread the email-thread slideout shows for a
 * proposal, or undefined when the proposal has no threaded email action.
 * Single definition shared by ProposalDetailPanel's open-thread handler and
 * AutoOpenFirstRecord's no-trigger-buttons fallback.
 */
export function findEmailActionForThread(
  proposal: ProposalDetail
): ProposedAction | undefined {
  return proposal.actions.find(
    a =>
      a.proposed_action_type_name === 'gmail_send' &&
      isGmailSendPayload(a.payload) &&
      (a.payload as GmailSendPayload).action_params.thread_id
  );
}
